import { currencyFormatter } from '@/helpers/currencyFormatter';
import { percentageFormatter } from '@/helpers/percentageFormatter';
import { DateTime } from 'luxon';

import { PromoValueType } from 'types';

export type PromoStatus = 'upcoming' | 'active' | 'expired';

export const PromoValueTypeLabels: Record<PromoValueType, string> = {
  [PromoValueType.fixed]: 'Fixed',
  [PromoValueType.percentOfCost]: '% of cost',
  [PromoValueType.perWatt]: 'Per watt',
};

export const PromoStatusLabels: Record<PromoStatus, string> = {
  upcoming: 'Upcoming',
  active: 'Active',
  expired: 'Expired',
};

export const PromoStatusTagColors: Record<PromoStatus, 'lumen' | 'success' | 'grey'> = {
  upcoming: 'lumen',
  active: 'success',
  expired: 'grey',
};

/**
 * Promo dates are UTC calendar days, so both sides are parsed and compared in UTC; a local-zone
 * parse would flip the status hours early or late for users away from UTC.
 */
export const getPromoStatus = (
  promo: { validFrom: string; validThrough?: string },
  now: DateTime = DateTime.utc(),
): PromoStatus => {
  const today = now.toUTC().startOf('day');
  if (today < DateTime.fromISO(promo.validFrom, { zone: 'utc' })) return 'upcoming';
  if (promo.validThrough && today > DateTime.fromISO(promo.validThrough, { zone: 'utc' })) return 'expired';
  return 'active';
};

/** A full selection collapses to "All ...", but only once the full lists have loaded. */
export const coversAllStates = (promoStates: string[], availableStates: ReadonlySet<string>): boolean =>
  availableStates.size > 0 && [...availableStates].every((state) => promoStates.includes(state));

export const coversAllUtilities = (promoUtilityKeys: string[], availableKeys: ReadonlySet<string>): boolean =>
  availableKeys.size > 0 && [...availableKeys].every((key) => promoUtilityKeys.includes(key));

export const formatPromoValue = (valueRule: { type: PromoValueType; value: number }): string => {
  switch (valueRule.type) {
    case PromoValueType.fixed:
      return currencyFormatter.format(valueRule.value);
    case PromoValueType.perWatt:
      return `${currencyFormatter.format(valueRule.value)}/W`;
    case PromoValueType.percentOfCost:
      return percentageFormatter.format(valueRule.value);
    default:
      return String(valueRule.value);
  }
};
